import React, { useEffect, useState } from 'react';
import api, { notifyUpdate } from '../../lib/api';
import { PERMISSIONS } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ShieldCheck } from 'lucide-react';

export default function AdminUloge() {
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', permissions: [] });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const load = () => api.get('/roles').then((res) => setRoles(res.data)).catch(() => {});
    load();
    window.addEventListener('store-updated', load);
    return () => window.removeEventListener('store-updated', load);
  }, []);

  const togglePerm = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key]
    }));
  };

  const save = async () => {
    if (!form.name) { toast.error('Unesite naziv uloge.'); return; }
    try {
      if (editId) {
        await api.put(`/roles/${editId}`, { name: form.name, permissions: form.permissions });
        toast.success('Uloga je ažurirana.');
      } else {
        await api.post('/roles', form);
        toast.success('Uloga je kreirana.');
      }
      notifyUpdate();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri spremanju.');
    }
  };

  const removeRole = async (id) => {
    try { await api.delete(`/roles/${id}`); toast.success('Uloga je obrisana.'); notifyUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Greška pri brisanju.'); }
  };

  return (
    <div data-testid="admin-uloge">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0b1a3d]">Uloge i dozvole</h1>
          <p className="text-sm text-slate-500">Upravljanje ulogama sistema i dozvolama pristupa pojedinim funkcionalnostima.</p>
        </div>
        <Button onClick={() => { setForm({ name: '', permissions: [] }); setEditId(null); setOpen(true); }} data-testid="add-role-btn"
          className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
          <Plus className="h-4 w-4 mr-2" /> Nova uloga
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {roles.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-100 p-6" data-testid={`role-card-${r.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#e8ecf7] flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-[#001752]" />
                </div>
                <div>
                  <p className="font-bold text-[#0b1a3d]">{r.name}</p>
                  {r.system && <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100 text-[10px] mt-0.5">Sistemska uloga</Badge>}
                </div>
              </div>
              <div className="flex gap-1.5">
                <Button size="icon" variant="outline" onClick={() => { setForm({ name: r.name, permissions: [...r.permissions] }); setEditId(r.id); setOpen(true); }}
                  className="h-8 w-8 border-slate-200 text-[#001752] hover:bg-[#001752] hover:text-white rounded-md transition-colors duration-200" data-testid={`edit-role-${r.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                {!r.system && (
                  <Button size="icon" variant="outline" onClick={() => removeRole(r.id)}
                    className="h-8 w-8 border-slate-200 text-red-600 hover:bg-red-600 hover:text-white rounded-md transition-colors duration-200" data-testid={`delete-role-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.permissions.length === 0 && <span className="text-xs text-slate-400">Bez administrativnih dozvola</span>}
              {r.permissions.map((p) => (
                <Badge key={p} variant="outline" className="border-slate-200 text-slate-600 text-[11px] font-normal">
                  {PERMISSIONS.find((x) => x.key === p)?.label || p}
                </Badge>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader><DialogTitle className="text-[#0b1a3d]">{editId ? 'Uredi ulogu' : 'Nova uloga'}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="text-slate-600 mb-1.5 block">Naziv uloge *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-form-name" className="border-slate-200" />
            </div>
            <div>
              <Label className="text-slate-600 mb-2 block">Dozvole</Label>
              <div className="space-y-2.5">
                {PERMISSIONS.map((p) => (
                  <div key={p.key} className="flex items-center gap-2.5">
                    <Checkbox id={p.key} checked={form.permissions.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} />
                    <Label htmlFor={p.key} className="text-sm text-slate-600 cursor-pointer font-normal">{p.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-200">Odustani</Button>
            <Button onClick={save} className="bg-[#001752] hover:bg-[#0a2a7a] text-white transition-colors duration-200" data-testid="role-form-save">Sačuvaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
